// LetterModal - preview + copy-to-clipboard for the two NJRC court letters
// (fine-waiver / time-served, and warrant-recall).
//
// Flow:
//   1. Open with a letter `kind` and a `jobId`.
//   2. Modal fetches the client-summary + petitioner-details for that job
//      to auto-fill what we can (name, DOB, cases, fines, address, etc.).
//   3. Renders a preview that LOOKS like a Word document, with every
//      bracketed placeholder bolded so the staffer can find-and-replace
//      them after pasting into Word.
//   4. "Copy to clipboard" button writes BOTH text/html and text/plain
//      to the system clipboard so Word picks up the bold formatting.
//
// One court at a time: if the participant has cases at multiple courts,
// the modal shows a court picker and the letter rebuilds when the
// staffer changes selection. NJRC sends one letter per court.
//
// No save, no API write - this is a pure client-side template generator.

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  copyLetterToClipboard,
  generateLetter,
  letterTitle,
  type LetterInputs,
  type LetterKind,
  type LetterOutput,
} from "../lib/letters";
import type { OpenCase } from "../pages/ClientSummaryPage";
import { toast } from "./Toaster";

interface ClientSummary {
  fullName: string;
  dob: string;
  totalCourtFinesOwed: number;
  openCases: OpenCase[];
}

interface PetitionerDetailsLite {
  streetAddress?: string;
  city?: string;
  state?: string;
  zip?: string;
  phone?: string;
  email?: string;
}

/** Subset of the NJ court directory entry the modal needs. Mirrors
 *  src/server/lib/courtDirectory.ts. */
interface NJCourt {
  code: string;
  name: string;
  county: string | null;
  phone: string;
  address: { street: string; city: string; state: string; zip: string };
  administrator: string | null;
  adminEmail?: string | null;
}

interface SyncMeta {
  lastFetchedAt: string | null;
  lastAttemptedAt: string | null;
  lastStatus: "success" | "fetch_failed" | "parse_failed" | "never_attempted";
  lastError: string | null;
  source: "live" | "disk-cache" | "bundled-only" | "none";
  courtCount: number;
}

interface Props {
  jobId: string;
  kind: LetterKind;
  onClose: () => void;
}

export function LetterModal({ jobId, kind, onClose }: Props): JSX.Element {
  const [summary, setSummary] = useState<ClientSummary | null>(null);
  const [details, setDetails] = useState<PetitionerDetailsLite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourt, setSelectedCourt] = useState<string>("__pick__");
  const [copied, setCopied] = useState(false);
  // Court directory state. `dirCourt` is the NJ court whose info gets
  // baked into the .docx (admin name + address). It's auto-detected
  // from the case's stored courtCode when the modal mounts; the staffer
  // can override via the "Change court" search panel below.
  const [dirCourt, setDirCourt] = useState<NJCourt | null>(null);
  const [courtPickerOpen, setCourtPickerOpen] = useState(false);
  const [courtQuery, setCourtQuery] = useState("");
  const [courtResults, setCourtResults] = useState<NJCourt[]>([]);
  const [courtOverridden, setCourtOverridden] = useState(false);
  // Sync metadata for the directory - drives "Updated Xm ago" caption
  // and the Sync-now button.
  const [syncMeta, setSyncMeta] = useState<SyncMeta | null>(null);
  const [syncing, setSyncing] = useState(false);

  const refreshSyncMeta = () => {
    fetch("/api/courts/sync-status", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => m && setSyncMeta(m as SyncMeta))
      .catch(() => { /* ignore */ });
  };
  useEffect(() => {
    refreshSyncMeta();
  }, []);

  const onSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await fetch("/api/courts/sync", {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      setSyncMeta(data as SyncMeta);
      if (data.ok === false) {
        toast(`Sheet sync failed: keeping previous data. ${data.error ?? ""}`, "danger");
      } else {
        toast(`Synced ${data.courts ?? 0} courts from the Google Sheet`, "success");
        // Re-fetch the auto-detected court info too in case it changed.
        if (selectedBucket && !courtOverridden) {
          const code = selectedBucket.cases.find((c) => c.courtCode)?.courtCode;
          if (code) {
            const r = await fetch(`/api/courts/${encodeURIComponent(code)}`, { credentials: "include" });
            if (r.ok) setDirCourt(await r.json() as NJCourt);
          }
        }
      }
    } catch (err) {
      toast(`Sync error: ${(err as Error).message}`, "danger");
    } finally {
      setSyncing(false);
    }
  };

  // Esc closes; lock body scroll while open.
  // We swallow Esc when it's pressed inside an input/textarea/select
  // (e.g. typing in the court-search box) so a stray Escape mid-edit
  // doesn't dismiss the modal and lose what the user just typed -
  // browsers usually use Esc to clear/blur the field, which is the
  // expected behavior we shouldn't override.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable) {
          return;
        }
      }
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // Fetch summary + details in parallel on mount.
  useEffect(() => {
    let alive = true;
    Promise.all([
      fetch(`/api/jobs/${jobId}/client-summary`, { credentials: "include" }).then((r) => {
        if (!r.ok) throw new Error(`summary HTTP ${r.status}`);
        return r.json() as Promise<ClientSummary>;
      }),
      fetch(`/api/jobs/${jobId}/petitioner-details`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : { details: null }))
        .catch(() => ({ details: null })),
    ])
      .then(([s, d]) => {
        if (!alive) return;
        setSummary(s);
        setDetails(d?.details ?? null);
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [jobId]);

  // Filter cases per letter type. Fine-waiver = anything with money owed
  // OR no balance but still open (court might still grant relief).
  // Warrant = only cases with active warrants.
  const eligibleCases = useMemo<OpenCase[]>(() => {
    if (!summary) return [];
    if (kind === "warrant") {
      return summary.openCases.filter((c) => c.hasWarrant);
    }
    // fineWaiver: every open case is eligible to be listed (court can
    // grant time-served on any of them)
    return summary.openCases;
  }, [summary, kind]);

  // Group eligible cases by court so the staffer can pick which court
  // this letter is for. (NJRC sends one letter per court.)
  const courtBuckets = useMemo(() => {
    const map = new Map<string, OpenCase[]>();
    for (const c of eligibleCases) {
      const key = c.courtName ?? "Unknown court";
      const arr = map.get(key) ?? [];
      arr.push(c);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([court, cases]) => ({ court, cases }));
  }, [eligibleCases]);

  // Auto-pick when there's only one court.
  useEffect(() => {
    if (courtBuckets.length === 1) {
      setSelectedCourt(courtBuckets[0]!.court);
    }
  }, [courtBuckets]);

  // Selected case bucket - single source of truth driving both letter
  // content + auto-detected court directory lookup.
  const selectedBucket = useMemo(() => {
    if (courtBuckets.length === 0) return null;
    if (selectedCourt === "__pick__") return courtBuckets[0]!;
    return courtBuckets.find((b) => b.court === selectedCourt) ?? courtBuckets[0]!;
  }, [courtBuckets, selectedCourt]);

  // Auto-detect the NJ court entry by looking up the bucket's first
  // case's courtCode. Resets the override on bucket change so picking
  // a different participant-court doesn't carry the override forward.
  useEffect(() => {
    if (!selectedBucket || courtOverridden) return;
    const code = selectedBucket.cases.find((c) => c.courtCode)?.courtCode;
    if (!code) {
      setDirCourt(null);
      return;
    }
    let alive = true;
    fetch(`/api/courts/${encodeURIComponent(code)}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => alive && setDirCourt(c as NJCourt | null))
      .catch(() => alive && setDirCourt(null));
    return () => {
      alive = false;
    };
  }, [selectedBucket, courtOverridden]);

  // Reset override when the case bucket changes (different participant-court).
  useEffect(() => {
    setCourtOverridden(false);
    setCourtPickerOpen(false);
  }, [selectedCourt]);

  // Court search - debounced, fires whenever the panel is open.
  useEffect(() => {
    if (!courtPickerOpen) return;
    const handle = setTimeout(() => {
      const params = new URLSearchParams({ q: courtQuery, limit: "12" });
      fetch(`/api/courts?${params}`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : { results: [] }))
        .then((d: { results: NJCourt[] }) => setCourtResults(d.results ?? []))
        .catch(() => setCourtResults([]));
    }, 120);
    return () => clearTimeout(handle);
  }, [courtPickerOpen, courtQuery]);

  // Generate the letter for the selected court (preview only - the
  // .docx download is built server-side from the same data).
  const letter: LetterOutput | null = useMemo(() => {
    if (!summary || !selectedBucket) return null;
    // When the directory matched (auto-detect or override), fold its
    // address + admin info into the inputs so the preview matches the
    // .docx download exactly.
    // Strip trailing credentials (", Esq.", ", CMCA") then take the
    // last whitespace-delimited token as the surname for the salutation.
    const adminLast = dirCourt?.administrator
      ? dirCourt.administrator.replace(/,\s*[A-Za-z.()\s]+$/, "").trim().split(/\s+/).pop() ?? undefined
      : undefined;
    const inputs: LetterInputs = {
      fullName: summary.fullName,
      dob: summary.dob,
      cases: selectedBucket.cases,
      totalFines: selectedBucket.cases.reduce((s, c) => s + (c.balanceDue ?? 0), 0),
      participantStreet: details?.streetAddress,
      participantCityStateZip:
        details?.city && details?.state
          ? `${details.city}, ${details.state}${details.zip ? ` ${details.zip}` : ""}`
          : undefined,
      participantPhone: details?.phone,
      participantEmail: details?.email,
      courtName: dirCourt?.name ?? (selectedBucket.court === "Unknown court" ? null : selectedBucket.court),
      courtAddressLines: dirCourt
        ? [dirCourt.address.street, `${dirCourt.address.city}, ${dirCourt.address.state} ${dirCourt.address.zip}`]
        : undefined,
      courtAdminName: dirCourt?.administrator ?? undefined,
      courtAdminLastName: adminLast,
      // Note: courtAdminTitle stays undefined in the preview - the .docx
      // download resolves it server-side via the gender table. Preview
      // shows bracketed [MR. / MS.] for the admin salutation.
    };
    return generateLetter(kind, inputs);
  }, [summary, details, selectedBucket, kind, dirCourt]);

  const onCopy = async () => {
    if (!letter) return;
    const ok = await copyLetterToClipboard(letter);
    if (ok) {
      setCopied(true);
      toast(`${letterTitle(kind)} copied to clipboard`, "success");
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast("Couldn't write to clipboard, copy manually", "danger");
    }
  };

  /** Download the populated .docx - server merges our content into the
   *  NJRC letterhead template (top header + left sidebar preserved). */
  const onDownloadDocx = () => {
    if (!summary || !selectedBucket) return;
    const params = new URLSearchParams();
    if (selectedBucket.court !== "Unknown court") params.set("court", selectedBucket.court);
    // When the staffer overrode the auto-detected court, pass the chosen
    // court code so the server uses that directory entry (admin name +
    // address) instead of looking up by the case's stored court code.
    if (courtOverridden && dirCourt) params.set("courtCode", dirCourt.code);
    const url = `/api/jobs/${jobId}/letters/${kind}.docx${params.toString() ? `?${params}` : ""}`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast(`${letterTitle(kind)} (.docx) downloaded, opens in Word with full letterhead`, "success");
  };

  const modal = (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={letterTitle(kind)}
        className="modal-card modal-card-wide"
      >
        <header className="modal-hero">
          <div className="modal-hero-eyebrow">Court Letter · Copy to Clipboard</div>
          <h2 className="modal-hero-title">{letterTitle(kind)}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="modal-hero-close"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>
        <div className="modal-body">
          {error && (
            <div
              style={{
                padding: 16,
                color: "var(--color-rose-hi)",
                background: "var(--color-rose-soft)",
                borderRadius: 8,
              }}
            >
              Couldn't load this person's data: {error}
            </div>
          )}

          {!error && !summary && (
            <div style={{ color: "var(--color-text-dim)", fontStyle: "italic" }}>
              Loading…
            </div>
          )}

          {summary && courtBuckets.length === 0 && (
            <div
              style={{
                padding: 18,
                background: "var(--color-bg-card-hi)",
                borderRadius: 10,
                color: "var(--color-text-muted)",
                fontFamily: "var(--font-serif)",
                fontStyle: "italic",
              }}
            >
              {kind === "warrant"
                ? "No active warrants on file for this participant, nothing to send a warrant-recall letter for."
                : "No open cases on file with eligible balances."}
            </div>
          )}

          {summary && courtBuckets.length > 0 && letter && (
            <>
              {/* Court picker (only if multiple courts) */}
              {courtBuckets.length > 1 && (
                <div
                  style={{
                    padding: "12px 16px",
                    background: "var(--color-bg-card-hi)",
                    borderRadius: 10,
                    marginBottom: 18,
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      color: "var(--color-text-muted)",
                    }}
                  >
                    Send to court
                  </span>
                  <select
                    value={selectedCourt === "__pick__" ? courtBuckets[0]!.court : selectedCourt}
                    onChange={(e) => setSelectedCourt(e.target.value)}
                    style={{
                      background: "var(--color-bg)",
                      color: "var(--color-text)",
                      border: "1px solid var(--color-line-hi)",
                      borderRadius: 6,
                      padding: "6px 10px",
                      fontSize: 14,
                      fontFamily: "var(--font-body)",
                      flex: "1 1 260px",
                    }}
                  >
                    {courtBuckets.map((b) => (
                      <option key={b.court} value={b.court}>
                        {b.court} ({b.cases.length} {b.cases.length === 1 ? "case" : "cases"})
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: 12, color: "var(--color-text-dim)", fontStyle: "italic" }}>
                    NJRC sends one letter per court, generate, copy, paste, send. Repeat for each.
                  </span>
                </div>
              )}

              {/* Court directory card - auto-detected from the case's
                  court code, with an instant-search override for cases
                  where the auto-detection lands on the wrong court. */}
              <CourtDirectoryCard
                dirCourt={dirCourt}
                overridden={courtOverridden}
                pickerOpen={courtPickerOpen}
                onTogglePicker={() => setCourtPickerOpen((v) => !v)}
                query={courtQuery}
                onQueryChange={setCourtQuery}
                results={courtResults}
                onPick={(c) => {
                  setDirCourt(c);
                  setCourtOverridden(true);
                  setCourtPickerOpen(false);
                  setCourtQuery("");
                }}
                onClearOverride={() => {
                  setCourtOverridden(false);
                }}
                syncMeta={syncMeta}
                syncing={syncing}
                onSyncNow={onSyncNow}
              />

              {/* What's filled / what's bracketed - quick legend */}
              <div
                style={{
                  display: "flex",
                  gap: 18,
                  marginBottom: 14,
                  fontSize: 12,
                  color: "var(--color-text-dim)",
                }}
              >
                <span>
                  <strong style={{ color: "var(--color-sage-hi)" }}>Auto-filled:</strong> name, DOB,
                  case numbers
                  {dirCourt ? ", court address, court admin" : ""}
                  {details?.streetAddress ? ", participant address" : ""}
                  {details?.phone ? ", phone" : ""}
                  {details?.email ? ", email" : ""}
                  {kind === "fineWaiver" ? ", fine total" : ""}, pronouns
                </span>
                {!dirCourt && (
                  <>
                    <span>·</span>
                    <span>
                      <strong style={{ color: "var(--color-orange-hi)" }}>You'll fill in:</strong>{" "}
                      court address, court administrator name
                    </span>
                  </>
                )}
              </div>

              {/* "Draft from last meeting" - pulls the most recent
                  diarized intake transcript through an LLM and produces
                  2-4 paragraphs of personal narrative the staffer can
                  paste into the letter as the "tell the story" section.
                  Hidden until the user clicks Draft, since most letters
                  don't need it. */}
              <DraftFromMeetingPanel personId={jobId} kind={kind} />

              {/* Letter preview - rendered HTML so the staffer sees what
                  they'll get in Word. Bold pops the placeholders. */}
              <div
                style={{
                  background: "#fafaf6",
                  color: "#111",
                  borderRadius: 10,
                  padding: "32px 40px",
                  border: "1px solid var(--color-line)",
                  maxHeight: "55vh",
                  overflow: "auto",
                  fontFamily: "'Times New Roman', Times, serif",
                  fontSize: 14,
                  lineHeight: 1.55,
                }}
                // The HTML payload is built entirely from our typed
                // template helpers (escapeHtml runs on every dynamic
                // string), so this is safe.
                dangerouslySetInnerHTML={{ __html: letter.html }}
              />
            </>
          )}
        </div>

        {/* - Footer - */}
        <footer
          style={{
            padding: "14px 24px",
            borderTop: "1px solid var(--color-line)",
            display: "flex",
            alignItems: "center",
            gap: 12,
            justifyContent: "flex-end",
            background: "var(--color-bg)",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--color-text-dim)", marginRight: "auto" }}>
            .docx merges into the NJRC letterhead template, opens in Word with header + sidebar intact.
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              color: "var(--color-text-muted)",
              border: "1px solid var(--color-line-hi)",
              borderRadius: 6,
              padding: "10px 16px",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onCopy}
            disabled={!letter}
            title="Copy plain text to clipboard (no letterhead)"
            style={{
              background: "transparent",
              color: copied ? "var(--color-sage-hi)" : "var(--color-text-muted)",
              border: `1px solid ${copied ? "var(--color-sage-hi)" : "var(--color-line-hi)"}`,
              borderRadius: 6,
              padding: "10px 16px",
              cursor: letter ? "pointer" : "not-allowed",
              fontSize: 13.5,
              fontWeight: 600,
              letterSpacing: "0.02em",
              opacity: letter ? 1 : 0.5,
              transition: "all 0.15s ease",
            }}
          >
            {copied ? "✓ Text copied" : "Copy text"}
          </button>
          <button
            type="button"
            onClick={onDownloadDocx}
            disabled={!letter}
            style={{
              background: "var(--color-orange-hi)",
              color: "white",
              border: "none",
              borderRadius: 6,
              padding: "10px 20px",
              cursor: letter ? "pointer" : "not-allowed",
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: "0.02em",
              opacity: letter ? 1 : 0.5,
              transition: "background 0.15s ease",
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M7 1v8M7 9l-3-3M7 9l3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2 12h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            Download .docx
          </button>
        </footer>
      </div>
    </>
  );

  return createPortal(modal, document.body);
}

// ─── Court directory card ─────────────────────────────────────────────

/** Shows the auto-detected (or staffer-overridden) NJ court that the
 *  letter will address. Has a slide-down search panel for picking a
 *  different court when the auto-detection lands on the wrong one. */
function CourtDirectoryCard({
  dirCourt,
  overridden,
  pickerOpen,
  onTogglePicker,
  query,
  onQueryChange,
  results,
  onPick,
  onClearOverride,
  syncMeta,
  syncing,
  onSyncNow,
}: {
  dirCourt: NJCourt | null;
  overridden: boolean;
  pickerOpen: boolean;
  onTogglePicker: () => void;
  query: string;
  onQueryChange: (q: string) => void;
  results: NJCourt[];
  onPick: (c: NJCourt) => void;
  onClearOverride: () => void;
  syncMeta: SyncMeta | null;
  syncing: boolean;
  onSyncNow: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        padding: "14px 16px",
        background: "var(--color-bg-card-hi)",
        border: "1px solid var(--color-line)",
        borderRadius: 10,
        marginBottom: 18,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "var(--color-text-dim)",
              fontWeight: 700,
              marginBottom: 6,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span>Court (sending letter to)</span>
            {dirCourt && (
              <span
                style={{
                  background: overridden
                    ? "var(--color-orange-soft)"
                    : "var(--color-sage-soft)",
                  color: overridden
                    ? "var(--color-orange-hi)"
                    : "var(--color-sage-hi)",
                  padding: "1px 7px",
                  borderRadius: 999,
                  fontSize: 9,
                  letterSpacing: "0.16em",
                  fontWeight: 700,
                }}
              >
                {overridden ? "MANUALLY PICKED" : "AUTO-DETECTED"}
              </span>
            )}
          </div>
          {dirCourt ? (
            <>
              <div
                style={{
                  fontFamily: "var(--font-serif)",
                  fontSize: 17,
                  fontWeight: 600,
                  color: "var(--color-text)",
                  letterSpacing: "-0.005em",
                }}
              >
                {dirCourt.name}
              </div>
              <div style={{ fontSize: 13, color: "var(--color-text-muted)", marginTop: 4 }}>
                {dirCourt.address.street}
                {" · "}
                {dirCourt.address.city}, {dirCourt.address.state} {dirCourt.address.zip}
              </div>
              {dirCourt.administrator && (
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--color-text-muted)",
                    marginTop: 4,
                  }}
                >
                  <span style={{ color: "var(--color-text-dim)" }}>Admin:</span>{" "}
                  <span style={{ color: "var(--color-text)" }}>
                    {dirCourt.administrator}
                  </span>
                  {dirCourt.adminEmail && (
                    <span style={{ color: "var(--color-text-dim)" }}>
                      {" · "}
                      {dirCourt.adminEmail}
                    </span>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 13, color: "var(--color-text-dim)", fontStyle: "italic" }}>
              No NJ-directory match. Court info will stay bracketed in the letter, pick one below to fill it in.
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexShrink: 0 }}>
          {overridden && (
            <button
              type="button"
              onClick={onClearOverride}
              title="Reset to the auto-detected court"
              style={{
                background: "transparent",
                color: "var(--color-text-muted)",
                border: "1px solid var(--color-line-hi)",
                borderRadius: 6,
                padding: "6px 10px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Reset
            </button>
          )}
          <button
            type="button"
            onClick={onTogglePicker}
            style={{
              background: pickerOpen
                ? "var(--color-orange-hi)"
                : "transparent",
              color: pickerOpen ? "white" : "var(--color-text-muted)",
              border: pickerOpen
                ? "1px solid var(--color-orange-hi)"
                : "1px solid var(--color-line-hi)",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {pickerOpen ? "Done" : "Change court"}
          </button>
        </div>
      </div>

      {pickerOpen && (
        <div style={{ marginTop: 14 }}>
          <input
            autoFocus
            type="text"
            placeholder="Search 575 NJ courts by name, city, county, admin, or code…"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            style={{
              width: "100%",
              background: "var(--color-bg)",
              color: "var(--color-text)",
              border: "1px solid var(--color-line-hi)",
              borderRadius: 6,
              padding: "10px 12px",
              fontSize: 14,
              fontFamily: "var(--font-body)",
            }}
          />
          {results.length > 0 && (
            <ul
              style={{
                listStyle: "none",
                margin: "8px 0 0",
                padding: 0,
                maxHeight: 240,
                overflow: "auto",
                border: "1px solid var(--color-line)",
                borderRadius: 6,
                background: "var(--color-bg)",
              }}
            >
              {results.map((c) => (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => onPick(c)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "9px 12px",
                      background: "transparent",
                      border: "none",
                      borderBottom: "1px solid var(--color-line)",
                      color: "var(--color-text)",
                      cursor: "pointer",
                    }}
                    className="hover:bg-[color:var(--color-bg-card-hi)]"
                  >
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 12, color: "var(--color-text-dim)", marginTop: 2 }}>
                      {c.address.city}, {c.county ?? ""} County
                      {c.administrator && (
                        <>
                          {" · "}
                          <span style={{ color: "var(--color-text-muted)" }}>
                            {c.administrator}
                          </span>
                        </>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {query && results.length === 0 && (
            <div
              style={{
                marginTop: 8,
                padding: "10px 12px",
                fontSize: 13,
                color: "var(--color-text-dim)",
                fontStyle: "italic",
              }}
            >
              No courts match that search.
            </div>
          )}
        </div>
      )}

      {/* Sync footer - last-fetched timestamp + manual "Sync now"
          button. Lets staff pull the Google Sheet immediately after an
          LSC has just updated a court contact, instead of waiting for
          the hourly poll. */}
      <SyncFooter syncMeta={syncMeta} syncing={syncing} onSyncNow={onSyncNow} />
    </div>
  );
}

function SyncFooter({
  syncMeta,
  syncing,
  onSyncNow,
}: {
  syncMeta: SyncMeta | null;
  syncing: boolean;
  onSyncNow: () => void;
}): JSX.Element {
  // Re-render every 30s so "synced 2m ago" doesn't sit stale until the
  // user happens to trigger another state change. The whole subtitle is
  // a function of (syncMeta, now), so a simple `now` tick re-runs the
  // format helper without needing any deeper restructuring.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  const subtitle = formatSyncSubtitle(syncMeta);
  const isStale = syncMeta?.lastStatus !== "success" && syncMeta?.lastStatus !== "never_attempted";
  return (
    <div
      style={{
        marginTop: 12,
        paddingTop: 10,
        borderTop: "1px solid var(--color-line)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontSize: 11.5,
        color: isStale ? "var(--color-rose-hi)" : "var(--color-text-dim)",
      }}
    >
      <span style={{ flex: 1 }}>{subtitle}</span>
      <button
        type="button"
        onClick={onSyncNow}
        disabled={syncing}
        title="Pull the latest court contacts from the Google Sheet right now"
        style={{
          background: "transparent",
          color: "var(--color-text-muted)",
          border: "1px solid var(--color-line-hi)",
          borderRadius: 5,
          padding: "4px 10px",
          fontSize: 11,
          fontWeight: 600,
          cursor: syncing ? "wait" : "pointer",
          letterSpacing: "0.04em",
        }}
      >
        {syncing ? "Syncing…" : "Sync now"}
      </button>
    </div>
  );
}

function formatSyncSubtitle(m: SyncMeta | null): string {
  if (!m) return "Court directory: loading…";
  if (m.lastStatus === "never_attempted") return "Court directory: not yet refreshed from sheet";
  if (m.lastStatus !== "success" && m.lastError) {
    return `Sheet sync failed: ${m.lastError}. Showing last-known-good (${m.courtCount} courts).`;
  }
  if (!m.lastFetchedAt) return `${m.courtCount} courts loaded`;
  const ago = formatRelative(new Date(m.lastFetchedAt));
  const sourceLabel =
    m.source === "live" ? "Google Sheet"
    : m.source === "disk-cache" ? "cached snapshot"
    : "bundled fallback";
  return `${m.courtCount} courts · synced ${ago} from ${sourceLabel}`;
}

function formatRelative(d: Date): string {
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}


// "Draft from last meeting" panel - sits above the letter preview.
// On click, fetches LLM-generated personal-narrative talking points
// pulled from the most recent diarized intake. The text is shown
// inline + has its own copy button. Designed to be paste-fodder -
// the LSC drops it into the letter's narrative section when sending.
function DraftFromMeetingPanel(props: { personId: string; kind: LetterKind }): JSX.Element {
  const [state, setState] = useState<"idle" | "loading" | "loaded" | "error">("idle");
  const [text, setText] = useState<string>("");
  const [meta, setMeta] = useState<{ endedAt: string | null; meetingType: string | null } | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string>("");

  const fetchDraft = async () => {
    setState("loading");
    setErr("");
    try {
      const r = await fetch(`/api/assessment/by-person/${props.personId}/draft-letter?kind=${encodeURIComponent(props.kind)}`);
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? `${r.status}`);
      }
      const j = (await r.json()) as { text: string; sourceEndedAt: string | null; sourceMeetingType: string | null };
      setText(j.text);
      setMeta({ endedAt: j.sourceEndedAt, meetingType: j.sourceMeetingType });
      setState("loaded");
    } catch (e) {
      setErr((e as Error).message);
      setState("error");
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { /* ignore */ }
  };

  if (state === "idle") {
    return (
      <div
        style={{
          marginBottom: 14,
          padding: "10px 14px",
          background: "var(--color-bg-card-hi)",
          border: "1px solid var(--color-line)",
          borderRadius: 10,
          display: "flex",
          alignItems: "center",
          gap: 12,
          fontSize: 12,
          color: "var(--color-text-muted)",
          fontFamily: "var(--font-body)",
        }}
      >
        <span style={{ flex: 1 }}>
          Want personal context from this person's most recent meeting? Draft talking points to paste into the letter.
        </span>
        <button
          type="button"
          onClick={fetchDraft}
          style={{
            background: "var(--color-accent, var(--color-orange-hi))",
            color: "#ffffff",
            border: "none",
            padding: "5px 12px",
            borderRadius: 999,
            fontFamily: "var(--font-body)",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.4,
            cursor: "pointer",
          }}
          title="Read the most recent meeting transcript and draft 2-4 paragraphs of personal narrative"
        >
          ✦ Draft from last meeting
        </button>
      </div>
    );
  }

  if (state === "loading") {
    return (
      <div
        style={{
          marginBottom: 14,
          padding: "12px 14px",
          background: "var(--color-bg-card-hi)",
          border: "1px solid var(--color-line)",
          borderRadius: 10,
          fontFamily: "var(--font-body)",
          fontSize: 12,
          color: "var(--color-text-muted)",
        }}
      >
        Drafting from last meeting transcript…
      </div>
    );
  }

  if (state === "error") {
    return (
      <div
        style={{
          marginBottom: 14,
          padding: "10px 14px",
          background: "var(--color-rose-soft)",
          border: "1px solid var(--color-rose-hi)",
          borderRadius: 10,
          fontFamily: "var(--font-body)",
          fontSize: 12,
          color: "var(--color-rose-hi)",
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        <span style={{ flex: 1 }}>
          {err === "no prior meeting on file for this person"
            ? "No prior meeting on file for this person, record one first to draft from it."
            : `Couldn't draft: ${err}`}
        </span>
        <button
          type="button"
          onClick={() => setState("idle")}
          style={{
            background: "transparent",
            border: "1px solid currentColor",
            color: "var(--color-rose-hi)",
            padding: "4px 10px",
            borderRadius: 999,
            fontSize: 11,
            cursor: "pointer",
          }}
        >
          Dismiss
        </button>
      </div>
    );
  }

  // loaded
  return (
    <div
      style={{
        marginBottom: 14,
        background: "var(--color-bg-card-hi)",
        border: "1px solid var(--color-line)",
        borderRadius: 10,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid var(--color-line)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          background: "var(--color-bg-card)",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            fontWeight: 700,
            color: "var(--color-text-muted)",
          }}
        >
          ✦ Drafted from last {meta?.meetingType ?? "meeting"}
          {meta?.endedAt ? ` · ${new Date(meta.endedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
        </span>
        <button
          type="button"
          onClick={onCopy}
          style={{
            marginLeft: "auto",
            background: copied ? "var(--color-sage-hi)" : "transparent",
            color: copied ? "#ffffff" : "var(--color-text-muted)",
            border: "1px solid var(--color-line-hi)",
            padding: "4px 10px",
            borderRadius: 999,
            fontFamily: "var(--font-body)",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            cursor: "pointer",
            transition: "background 140ms ease, color 140ms ease",
          }}
        >
          {copied ? "✓ Copied" : "Copy"}
        </button>
        <button
          type="button"
          onClick={fetchDraft}
          style={{
            background: "transparent",
            color: "var(--color-text-dim)",
            border: "1px solid var(--color-line)",
            padding: "4px 10px",
            borderRadius: 999,
            fontFamily: "var(--font-body)",
            fontSize: 10,
            fontWeight: 600,
            cursor: "pointer",
          }}
          title="Re-generate the draft (uses cached version unless meeting transcript changed)"
        >
          ↻
        </button>
      </div>
      <div
        style={{
          padding: "16px 18px",
          fontFamily: "Fraunces, var(--font-serif), serif",
          fontSize: 14,
          color: "var(--color-text)",
          lineHeight: 1.65,
          whiteSpace: "pre-wrap",
          maxHeight: 360,
          overflowY: "auto",
        }}
      >
        {text}
      </div>
    </div>
  );
}
